import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import {
  getUserStoresStatusRepository,
  setStoreStatusRepository,
  getStoreStatusByDateRepository,
  getStoreStatusDashboardRepository,
  getStoreStatusHistoryRepository,
  getAllStoreStatusesRepository,
} from "features/ms6/data/ms6.repository";
import {
  SetStoreStatusParam,
  StoreStatusFilters,
  UserStore,
  StoreStatusData,
  StoreStatusDashboardStats,
} from "features/ms6/core/ms6.param";

export enum StoreStatusState {
  initial = "initial",
  inProgress = "inProgress",
  success = "success",
  fail = "fail",
}

// FIXED: Define proper types
export type SortDirection = "asc" | "desc";
export type SortField =
  | "status_date"
  | "store_name"
  | "status"
  | "created_at"
  | "updated_at";

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
}

interface SortingInfo {
  field: SortField;
  direction: SortDirection;
}

interface InitialState {
  status: StoreStatusState;
  message: string;
  userStores: UserStore[];
  storeStatuses: StoreStatusData[];
  dashboardStats: StoreStatusDashboardStats | null;
  storeHistory: Array<any>;
  allStoreStatuses: Array<any>;
  pagination: PaginationInfo;
  sorting: SortingInfo;
  isAdmin: boolean;
  hasPermission: boolean;
  userType: string | null;
  permissionError: string | null;
}

const initialState: InitialState = {
  status: StoreStatusState.initial,
  message: "",
  userStores: [],
  storeStatuses: [],
  dashboardStats: null,
  storeHistory: [],
  allStoreStatuses: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    pageSize: 10,
  },
  sorting: {
    field: "status_date",
    direction: "desc",
  },
  isAdmin: false,
  hasPermission: false,
  userType: null,
  permissionError: null,
};

// FIXED: Proper parameter types
interface GetAllStoreStatusesParams {
  page?: number;
  pageSize?: number;
  sortField?: SortField;
  sortDirection?: SortDirection;
}

// NEW: Get all store statuses (for admin)
export const getAllStoreStatuses = createAsyncThunk(
  "storeStatus/getAllStoreStatuses",
  async (params: GetAllStoreStatusesParams, { rejectWithValue }) => {
    try {
      const response = await getAllStoreStatusesRepository(params);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch all store statuses"
        );
      }
      return rejectWithValue("Failed to fetch all store statuses");
    }
  }
);

// FIXED: getUserStores async thunk
export const getUserStores = createAsyncThunk(
  "storeStatus/getUserStores",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserStoresStatusRepository();
      return response.data; // This will be the backend response structure
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch user stores"
        );
      }
      return rejectWithValue("Failed to fetch user stores");
    }
  }
);

export const setStoreStatus = createAsyncThunk(
  "storeStatus/setStoreStatus",
  async (statusData: SetStoreStatusParam, { rejectWithValue }) => {
    try {
      const response = await setStoreStatusRepository(statusData);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to set store status"
        );
      }
      return rejectWithValue("Failed to set store status");
    }
  }
);

export const getStoreStatusByDate = createAsyncThunk(
  "storeStatus/getStoreStatusByDate",
  async (filters: StoreStatusFilters, { rejectWithValue }) => {
    try {
      const response = await getStoreStatusByDateRepository(filters);
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch store statuses"
        );
      }
      return rejectWithValue("Failed to fetch store statuses");
    }
  }
);

export const getDashboardData = createAsyncThunk(
  "storeStatus/getDashboardData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getStoreStatusDashboardRepository();
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch dashboard data"
        );
      }
      return rejectWithValue("Failed to fetch dashboard data");
    }
  }
);

export const getStoreHistory = createAsyncThunk(
  "storeStatus/getStoreHistory",
  async (storeId: number, { rejectWithValue }) => {
    try {
      const response = await getStoreStatusHistoryRepository(storeId);
      return response.data.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(
          error.response?.data?.message || "Failed to fetch store history"
        );
      }
      return rejectWithValue("Failed to fetch store history");
    }
  }
);

const storeStatusSlice = createSlice({
  name: "storeStatus",
  initialState,
  reducers: {
    resetStoreStatus: (state) => {
      state.status = StoreStatusState.initial;
      state.message = "";
    },
    clearStoreStatuses: (state) => {
      state.storeStatuses = [];
    },
    // FIXED: Proper typing for pagination
    setPagination: (state, action: { payload: Partial<PaginationInfo> }) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    // FIXED: Proper typing for sorting
    setSorting: (state, action: { payload: SortingInfo }) => {
      state.sorting = action.payload;
    },
  },
  extraReducers: (builder) => {
    // FIXED: Get User Stores with correct payload structure
    builder
      .addCase(getUserStores.pending, (state) => {
        state.status = StoreStatusState.inProgress;
        state.permissionError = null;
      })
      .addCase(getUserStores.fulfilled, (state, action) => {
        // action.payload is the backend response structure
        if (action.payload.status === "error") {
          // Handle permission denied case
          state.status = StoreStatusState.fail;
          state.message = action.payload.message || "Access denied";
          state.hasPermission = false;
          state.permissionError = action.payload.message || "Access denied";
          state.userStores = [];
          state.isAdmin = false;
          state.userType = null;
        } else {
          // Handle successful case
          state.status = StoreStatusState.success;
          state.userStores = action.payload.data; // This is the UserStore[] array
          state.isAdmin = action.payload.is_admin;
          state.userType = action.payload.user_type;
          state.hasPermission = action.payload.has_permission;
          state.permissionError = null;
          state.message = "";
        }
      })
      .addCase(getUserStores.rejected, (state, action) => {
        state.status = StoreStatusState.fail;
        state.message = action.payload as string;
        state.hasPermission = false;
        state.permissionError = action.payload as string;
      });

    // Set Store Status
    builder
      .addCase(setStoreStatus.pending, (state) => {
        state.status = StoreStatusState.inProgress;
      })
      .addCase(setStoreStatus.fulfilled, (state, action) => {
        state.status = StoreStatusState.success;
        state.message = action.payload.message;
      })
      .addCase(setStoreStatus.rejected, (state, action) => {
        state.status = StoreStatusState.fail;
        state.message = action.payload as string;
        // Check if it's a permission error
        if ((action.payload as string).includes("Access denied")) {
          state.hasPermission = false;
          state.permissionError = action.payload as string;
        }
      });

    // Get Store Status by Date
    builder
      .addCase(getStoreStatusByDate.fulfilled, (state, action) => {
        state.storeStatuses = action.payload || []; // Handle null/undefined
      })
      .addCase(getStoreStatusByDate.rejected, (state, action) => {
        console.log(
          "Store status fetch returned empty or failed:",
          action.payload
        );
        state.storeStatuses = []; // Set empty array instead of error

        // Only set permission error if it's actually a permission issue
        if ((action.payload as string)?.includes("Access denied")) {
          state.status = StoreStatusState.fail;
          state.message = action.payload as string;
          state.hasPermission = false;
          state.permissionError = action.payload as string;
        }
      });

    // Get Dashboard Data
    builder
      .addCase(getDashboardData.fulfilled, (state, action) => {
        state.dashboardStats = action.payload || {
          today_stats: [],
          weekly_trend: [],
        };
      })
      .addCase(getDashboardData.rejected, (state, action) => {
        console.log(
          "Dashboard data fetch returned empty or failed:",
          action.payload
        );
        state.dashboardStats = { today_stats: [], weekly_trend: [] };

        // Only set permission error if it's actually a permission issue
        if ((action.payload as string)?.includes("Access denied")) {
          state.status = StoreStatusState.fail;
          state.message = action.payload as string;
          state.hasPermission = false;
          state.permissionError = action.payload as string;
        }
      });

    // Get Store History
    builder
      .addCase(getStoreHistory.fulfilled, (state, action) => {
        state.storeHistory = action.payload;
      })
      .addCase(getStoreHistory.rejected, (state, action) => {
        state.status = StoreStatusState.fail;
        state.message = action.payload as string;
        if ((action.payload as string).includes("Access denied")) {
          state.hasPermission = false;
          state.permissionError = action.payload as string;
        }
      });

    // Get All Store Statuses (for admin)
    builder
      .addCase(getAllStoreStatuses.pending, (state) => {
        state.status = StoreStatusState.inProgress;
      })
      .addCase(getAllStoreStatuses.fulfilled, (state, action) => {
        state.status = StoreStatusState.success;
        state.allStoreStatuses = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllStoreStatuses.rejected, (state, action) => {
        state.status = StoreStatusState.fail;
        state.message = action.payload as string;
        if ((action.payload as string).includes("Access denied")) {
          state.hasPermission = false;
          state.permissionError = action.payload as string;
        }
      });
  },
});

export const {
  resetStoreStatus,
  clearStoreStatuses,
  setPagination,
  setSorting,
} = storeStatusSlice.actions;

export const selectStoreStatus = (state: RootState) => state.storeStatus;

export default storeStatusSlice.reducer;
